import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Provider } from 'react-redux';
import store from './store';
import { loadUser } from './actions/authActions';
import { setAuthToken } from './utils/setAuthToken';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';

// Page Components
import HomePage from './pages/HomePage';
import ProductListPage from './pages/ProductListPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PasswordResetPage from './pages/PasswordResetPage';
import ProfilePage from './pages/ProfilePage';
import OrdersPage from './pages/OrdersPage';
import OrderDetailPage from './pages/OrderDetailPage';
import AboutPage from './pages/AboutPage';
import TeamPage from './pages/TeamPage';
import InventoryPage from './pages/admin/InventoryPage';
import WarehouseDetailPage from './pages/admin/WarehouseDetailPage';
import WarehouseFormPage from './pages/admin/WarehouseFormPage';
import TransactionHistoryinvPage from './pages/admin/TransactionHistoryinvPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
// Admin Pages
import AdminDashboardPage from './pages/admin/DashboardPage';
import AdminProductsPage from './pages/admin/ProductsPage';
import AdminProductEditPage from './pages/admin/ProductEditPage';
import AdminCategoriesPage from './pages/admin/CategoriesPage';
import AdminOrdersPage from './pages/admin/OrdersPage';
import AdminUsersPage from './pages/admin/UsersPage';
import AnalyticsPage from './pages/admin/AnalyticsPage';
import SuperAdminRoute from './components/routing/SuperAdminRoute';
import MobileQuickOps from './pages/admin/MobileQuickOps';

// Route Protection Components
import PrivateRoute from './components/routing/PrivateRoute';
import AdminRoute from './components/routing/AdminRoute';
import SmartOutbound from './components/inventory/SmartOutbound';

// Check if token exists
if (localStorage.token) {
  setAuthToken(localStorage.token);
}

const App = () => {
  useEffect(() => {
    store.dispatch(loadUser());
  }, []);
  
  return (
    <Provider store={store}>
      <Router>
        <div className="flex flex-col min-h-screen">
          <Header />
          <main className="container mx-auto px-4 py-8 flex-grow">
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/products" element={<ProductListPage />} />
              <Route path="/product/:id" element={<ProductDetailPage />} />
              <Route path="/cart" element={<CartPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/reset-password" element={<PasswordResetPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/team" element={<TeamPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              {/* Protected Routes */}
              <Route 
                path="/checkout" 
                element={
                  <PrivateRoute>
                    <CheckoutPage />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <PrivateRoute>
                    <ProfilePage />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/orders" 
                element={
                  <PrivateRoute>
                    <OrdersPage />
                  </PrivateRoute>
                } 
              />
              <Route 
                path="/order/:id" 
                element={
                  <PrivateRoute>
                    <OrderDetailPage />
                  </PrivateRoute>
                } 
              />
              
              {/* Admin Routes */}
              <Route 
                path="/admin" 
                element={
                  <AdminRoute>
                    <AdminDashboardPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/products" 
                element={
                  <AdminRoute>
                    <AdminProductsPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/product/:id" 
                element={
                  <AdminRoute>
                    <AdminProductEditPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/inventory" 
                element={
                  <AdminRoute>
                    <InventoryPage />
                  </AdminRoute>
                } 
              />
              
              <Route 
                path="/admin/inventory/transactions" 
                element={
                  <SuperAdminRoute>
                    <TransactionHistoryinvPage />
                  </SuperAdminRoute>
                } 
              />

              <Route 
                path="/admin/inventory/transactions/:warehouseId" 
                element={
                  <SuperAdminRoute>
                    <TransactionHistoryinvPage />
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/categories" 
                element={
                  <AdminRoute>
                    <AdminCategoriesPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/orders" 
                element={
                  <AdminRoute>
                    <AdminOrdersPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/users" 
                element={
                  <SuperAdminRoute>
                    <AdminUsersPage />
                  </SuperAdminRoute>
                } 
              />
              <Route 
                path="/admin/inventory/warehouse/new" 
                element={
                  <AdminRoute>
                    <WarehouseFormPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/inventory/outbound" 
                element={
                  <AdminRoute>
                    <SmartOutbound />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/inventory/warehouse/:id" 
                element={
                  <AdminRoute>
                    <WarehouseDetailPage />
                  </AdminRoute>
                } 
              />
              <Route 
                path="/admin/inventory/warehouse/:id/edit" 
                element={
                  <AdminRoute>
                    <WarehouseFormPage />
                  </AdminRoute>
                } 
              />

              <Route
                path="/admin/mobile"
                element={
                  <AdminRoute>
                    <MobileQuickOps />
                  </AdminRoute>
                }
              />              

              <Route 
                path="/admin/analytics" 
                element={
                  <SuperAdminRoute>
                    <AnalyticsPage />
                  </SuperAdminRoute>
                } 
              />
            </Routes>
          </main>
          <Footer />
        </div>
      </Router>
    </Provider>
  );
};

export default App;